import React, { useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import {
  getCandidateValuesText,
  getLeftBarColorClassPercentile,
  getRightBarColorClassPercentile,
} from '../shared/report-util';
import { useAuth } from '../../../providers/auth-provider';
import { getPercentileAttributesConstants, isStaffingCandidate } from '../../../api/dashboards-api';
import { REPORT_TYPE } from '../../../utils/constants';

function CandidateProfileAssessments({
  candidate,
  displayPercentile = true,
  remoteStaffing = false,
  reportType = REPORT_TYPE.coaching,
}) {
  const { isSelect } = useAuth();
  const [percentileAttributes, setPercentileAttributes] = useState(getPercentileAttributesConstants().default);
  const [nValues, setNValues] = useState(18);
  const isStaffing = isStaffingCandidate(candidate);

  useEffect(() => {
    if (candidate) {
      if (isStaffing) {
        setNValues(33);
        if (remoteStaffing) {
          setPercentileAttributes(getPercentileAttributesConstants().remote);
        } else {
          setPercentileAttributes(getPercentileAttributesConstants().onsite);
        }
      }
    }
  }, [candidate]);

  const getAssessmentContent = (title, value, description, displayPercentile) => {
    return (
      <Stack className="w-50">
        <div className="text-bolder">{title}</div>
        <Stack className="chart-data" direction="horizontal">
          <>
            <div style={{ width: '80%' }}>
              <div className="progress-chart">
                <div
                  className={`chart-bar chart-bar-left ${getLeftBarColorClassPercentile(value, reportType)}`}
                  style={{ width: `${value}%` }}
                />
                <div
                  className={`chart-bar chart-bar-right ${getRightBarColorClassPercentile(value, reportType)}`}
                  style={{ width: `${100 - value}%` }}
                />
              </div>
            </div>
            {displayPercentile && (
              <div className="d-flex w-25 h-100 justify-content-center align-items-center">
                <div className="text-larger text-bolder text-end">{`${value}%`}</div>
              </div>
            )}
          </>
        </Stack>
        <div className="candidate-profile-assessment-description">{description}</div>
      </Stack>
    );
  };

  return (
    <>
      {!isStaffing ? (
        <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
          {candidate && (
            <>
              <Row className="justify-content-center pt-4">Predictive Assessments</Row>
              <Row className="justify-content-center w-25 mx-auto pt-3">
                <hr style={{ borderTop: '2px solid' }} />
              </Row>
              <Row className="w-100 mx-auto justify-content-center">
                <Stack direction="horizontal" className="candidate-profile-assessment-content" gap={4}>
                  {getAssessmentContent(
                    'Acumen',
                    candidate.percentiles.acumen,
                    'Acumen assessment evaluates the ability to make accurate judgments and quick decisions using memory, attention, and recall skills',
                    displayPercentile
                  )}
                  {getAssessmentContent(
                    `${getCandidateValuesText(isSelect(), true)} Fit`,
                    candidate.percentiles.values,
                    `${getCandidateValuesText(
                      isSelect(),
                      true
                    )} Fit assesses the cultural fit of candidates on ${nValues} distinct values`,
                    displayPercentile
                  )}
                </Stack>
              </Row>
              <Row className="w-100 mx-auto justify-content-center pt-4">
                <Stack direction="horizontal" className="candidate-profile-assessment-content" gap={4}>
                  {getAssessmentContent(
                    'Personality',
                    candidate.percentiles.personality,
                    "Personality assesses key traits of an individual's personality that drive performance and loyalty",
                    displayPercentile
                  )}
                  {getAssessmentContent(
                    'Judgment',
                    candidate.percentiles.judgment,
                    "Judgment assesses candidate's critical thinking, decision-making, and judgment skills in leadership situations commonly encountered at work",
                    displayPercentile
                  )}
                </Stack>
              </Row>
              <Row className="w-100 mx-auto justify-content-center pt-4">
                <Stack direction="horizontal" className="candidate-profile-assessment-content" gap={4}>
                  {getAssessmentContent(
                    'Experience',
                    candidate.percentiles.experience,
                    'Experience assesses critical candidate work experiences and skills that drive job performance',
                    displayPercentile
                  )}
                  <Stack className="w-50" />
                </Stack>
              </Row>
            </>
          )}
        </Container>
      ) : (
        <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
          {candidate && (
            <>
              <Row className="justify-content-center pt-4">Predictive Assessments</Row>
              <Row className="justify-content-center w-25 mx-auto pt-3">
                <hr style={{ borderTop: '2px solid' }} />
              </Row>
              <Row className="w-100 mx-auto justify-content-center">
                <Stack direction="horizontal" className="candidate-profile-assessment-content" gap={4}>
                  {getAssessmentContent(
                    'Personality',
                    candidate.percentiles[percentileAttributes.personality],
                    "Personality assesses key traits of an individual's personality that drive performance and loyalty",
                    false
                  )}
                  {getAssessmentContent(
                    'Experience',
                    candidate.percentiles[percentileAttributes.experience],
                    'Experience assesses critical candidate work experiences and skills that drive job performance',
                    false
                  )}
                </Stack>
              </Row>
              <Row className="w-100 mx-auto justify-content-center pt-4">
                <Stack direction="horizontal" className="candidate-profile-assessment-content" gap={4}>
                  {getAssessmentContent(
                    `${getCandidateValuesText(isSelect(), true)} Fit`,
                    candidate.percentiles[percentileAttributes.values],
                    `${getCandidateValuesText(
                      isSelect(),
                      true
                    )} Fit assesses the cultural fit of candidates on ${nValues} distinct values`,
                    false
                  )}
                  {!remoteStaffing ? (
                    <>
                      {getAssessmentContent(
                        'Judgment',
                        candidate.percentiles[percentileAttributes.judgment],
                        "Judgment assesses candidate's critical thinking, decision-making, and judgment skills in leadership situations commonly encountered at work",
                        false
                      )}
                    </>
                  ) : (
                    <Stack className="w-50" />
                  )}
                </Stack>
              </Row>
            </>
          )}
        </Container>
      )}
    </>
  );
}

export default CandidateProfileAssessments;
